import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Send, Mail, MessageCircle, User } from 'lucide-react';
import { useMembershipStatus, MembershipBlockedOverlay, ConsentLimitOverlay } from '../components/MembershipGuard';

export default function NuevaFirma() {
  const { API_URL } = useContext(AppContext);
  const { isBlocked, isLimitReached, loading: membershipLoading, refetchMembership } = useMembershipStatus();
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    template_id: '',
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    tipo_documento: 'DNI',
    numero_documento: '',
    fecha_nacimiento: '',
    email: '',
    codigo_pais: '57',
    numero_whatsapp: '',
    nombre_profesional: '',  // Nuevo campo opcional
    es_menor_edad: false,
    nombre_menor: '',
    numero_documento_menor: ''
  });
  const [createdRequest, setCreatedRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError(false);
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/consents/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      if (response.data.length > 0) {
        setFormData(prev => ({ ...prev, template_id: response.data[0].id }));
      }
    } catch (error) {
      // Distinguish "failed to load" from "genuinely no templates" — showing the empty
      // state here would wrongly tell the user to go create a template that already exists.
      setTemplatesError(true);
      toast.error('Error al cargar plantillas');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.post(`${API_URL}/consents/create-request`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreatedRequest(response.data);
      toast.success('Solicitud creada exitosamente');
      // Creating a request consumes one of the plan's consentimientos — refresh the
      // shared membership status so the remaining-count/limit-reached state stays accurate.
      refetchMembership(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLink = async (via) => {
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.post(
        `${API_URL}/consents/send-link`,
        { request_id: createdRequest.id, send_via: via },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (via === 'whatsapp' && response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      
      toast.success(response.data.message);
      
      setFormData({
        template_id: templates[0]?.id || '',
        nombre: '',
        primer_apellido: '',
        segundo_apellido: '',
        tipo_documento: 'DNI',
        numero_documento: '',
        fecha_nacimiento: '',
        email: '',
        codigo_pais: '57',
        numero_whatsapp: '',
        es_menor_edad: false,
        nombre_menor: '',
        numero_documento_menor: ''
      });
      setCreatedRequest(null);
    } catch (error) {
      toast.error('Error al enviar link');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <DashboardLayout>
      <div data-testid="nueva-firma-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">Nueva Firma de Consentimiento</h1>
          <p className="text-slate-600">Ingresa los datos del paciente para generar el consentimiento</p>
        </div>

        {membershipLoading || templatesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : isBlocked ? (
          <MembershipBlockedOverlay mode="full" />
        ) : isLimitReached ? (
          <ConsentLimitOverlay />
        ) : templatesError ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="templates-error">
            <p className="text-red-800 font-medium">No se pudieron cargar las plantillas de consentimiento.</p>
            <button
              onClick={fetchTemplates}
              className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Reintentar
            </button>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center" data-testid="no-templates-warning">
            <p className="text-amber-800 font-medium">No hay plantillas de consentimiento configuradas.</p>
            <p className="text-amber-600 text-sm mt-1">Ve a Configuración de Consentimientos para crear una plantilla.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Plantilla de Consentimiento
                </label>
                <select
                  name="template_id"
                  value={formData.template_id}
                  onChange={handleChange}
                  data-testid="select-template"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                  required
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.nombre_procedimiento}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombres <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    data-testid="input-nombre"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Primer Apellido <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="primer_apellido"
                    value={formData.primer_apellido}
                    onChange={handleChange}
                    data-testid="input-primer-apellido"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Segundo Apellido <span className="text-slate-400 text-xs">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    name="segundo_apellido"
                    value={formData.segundo_apellido}
                    onChange={handleChange}
                    data-testid="input-segundo-apellido"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Tipo de Documento <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="tipo_documento"
                    value={formData.tipo_documento}
                    onChange={handleChange}
                    data-testid="select-tipo-documento"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  >
                    <option value="DNI">DNI</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="NIT">NIT</option>
                    <option value="RUT">RUT</option>
                    <option value="Cedula">Cédula</option>
                    <option value="Otro">Otro Documento</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Número de Documento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="numero_documento"
                    value={formData.numero_documento}
                    onChange={handleChange}
                    data-testid="input-numero-documento"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fecha de Nacimiento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="fecha_nacimiento"
                    value={formData.fecha_nacimiento}
                    onChange={handleChange}
                    data-testid="input-fecha-nacimiento"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Correo Electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="input-email"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Código País <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="codigo_pais"
                    value={formData.codigo_pais}
                    onChange={handleChange}
                    data-testid="input-codigo-pais"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    placeholder="57"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Número de WhatsApp <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="numero_whatsapp"
                    value={formData.numero_whatsapp}
                    onChange={handleChange}
                    data-testid="input-whatsapp"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                    placeholder="3001234567"
                    required
                  />
                </div>
              </div>

              {/* Profesional que realizará la atención */}
              <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <User size={16} className="inline mr-2 text-violet-600" />
                  Nombre del Profesional que Realizará la Atención <span className="text-slate-400 text-xs">(opcional)</span>
                </label>
                <input
                  type="text"
                  name="nombre_profesional"
                  value={formData.nombre_profesional}
                  onChange={handleChange}
                  data-testid="input-nombre-profesional"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  placeholder="Nombre completo del profesional"
                />
              </div>

              {/* Menor de Edad Section */}
              <div className="pt-6 border-t border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="es_menor_edad"
                    name="es_menor_edad"
                    checked={formData.es_menor_edad}
                    onChange={(e) => setFormData({ ...formData, es_menor_edad: e.target.checked })}
                    data-testid="checkbox-menor-edad"
                    className="w-5 h-5 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
                  />
                  <label htmlFor="es_menor_edad" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Menor de Edad o Imposibilitado de Firmar
                  </label>
                </div>

                {formData.es_menor_edad && (
                  <div className="space-y-4 pl-8 border-l-2 border-violet-200">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Nombre del Menor de Edad o Imposibilitado de Firmar
                      </label>
                      <input
                        type="text"
                        name="nombre_menor"
                        value={formData.nombre_menor}
                        onChange={handleChange}
                        data-testid="input-nombre-menor"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                        placeholder="Nombre completo"
                        required={formData.es_menor_edad}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Número de Documento
                      </label>
                      <input
                        type="text"
                        name="numero_documento_menor"
                        value={formData.numero_documento_menor}
                        onChange={handleChange}
                        data-testid="input-documento-menor"
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-slate-50"
                        placeholder="Número de documento"
                        required={formData.es_menor_edad}
                      />
                    </div>
                  </div>
                )}
              </div>

              {!createdRequest && (
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-button"
                    className="px-8 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 shadow-lg shadow-violet-500/20 active:scale-95"
                  >
                    {loading ? 'Creando...' : 'Crear Solicitud'}
                  </button>
                </div>
              )}
            </form>

            {createdRequest && (
              <div className="mt-8 p-6 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="send-link-section">
                <h3 className="text-lg font-bold text-emerald-900 mb-4">¡Solicitud Creada!</h3>
                <p className="text-emerald-700 mb-6">Selecciona cómo enviar el link de firma al paciente:</p>
                
                <div className="flex gap-4">
                  <button
                    onClick={() => handleSendLink('email')}
                    data-testid="send-email-button"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all shadow-lg active:scale-95"
                  >
                    <Mail size={20} />
                    Enviar por Correo
                  </button>

                  <button
                    onClick={() => handleSendLink('whatsapp')}
                    data-testid="send-whatsapp-button"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all shadow-lg active:scale-95"
                  >
                    <MessageCircle size={20} />
                    Enviar por WhatsApp
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
